/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useMemo, useState } from "react";
import { When } from "react-if";
import styled, { css } from "styled-components";
import {
  Button,
  FlexContainer,
  Message,
  Spacer,
  Text,
  theme,
} from "@nordcloud/gnui";
import { useCurrency } from "~/hooks";
import { Can, ACTIONS } from "~/services/auth/RBAC";
import { formatMoney, sum } from "~/tools";
import { splitToQuarter } from "../helpers";
import { useBudgetBreakdown, useBudgetBreakdownActions } from "../hooks";
import { BudgetBreakdownActions } from "./BudgetBreakdownActions";
import { BudgetCell } from "./BudgetCell";
import { GrowthPlanNumberInput } from "./GrowthPlanNumberInput";
import { YearlyBudget } from "./YearlyBudget";

type Props = {
  monthly: number[];
  childrenMonthly?: number[];
  isBudgetGenerated: boolean;
  shouldAllowRestoration: boolean;
  warningCaption?: string;
  year: string;
  onEdit: (values: number[]) => Promise<void>;
  onChangedBudget: (values: number[]) => void;
  onRestoreBudget: (initial: boolean) => void;
  onReset: (cached: number[][]) => void;
};

// TODO: split into smaller components
export function BudgetBreakdown({
  monthly,
  childrenMonthly,
  warningCaption,
  isBudgetGenerated,
  shouldAllowRestoration,
  year,
  onEdit,
  onChangedBudget,
  onRestoreBudget,
  onReset,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [growthRate, setGrowthRate] = useState(0);
  const [showApply, setShowApply] = useState(true);
  const [visibleYear, setVisibleYear] = useState(year);
  const [isRestored, setIsRestored] = useState(false);

  const { currency } = useCurrency();

  const quarterBudgets = useMemo(() => splitToQuarter(monthly), [monthly]);

  const [newBudget, setNewBudget] = useState<number[][]>(quarterBudgets);

  const yearlyBudget = useMemo(() => sum(newBudget.flat()), [newBudget]);

  const [previousBudget, setPreviousBudget] = useState(yearlyBudget);

  const {
    hasAnyChanged,
    growthRateBudget,
    growthRateBudgetCoefficient,
    quarters,
    quarterlyBudgets,
  } = useBudgetBreakdown({
    monthly,
    newBudget,
    growthRate,
    yearlyBudget,
  });

  const {
    updateBudget,
    cancel,
    saveBudget,
    hasChanged,
    yearlyBudgetChanged,
    applyGrowthRateBudget,
    hasAnyChangedComparedToCache,
    setLastEdited,
  } = useBudgetBreakdownActions({
    newBudget,
    quarterBudgets,
    growthRateBudgetCoefficient,
    setNewBudget,
    setEditing,
    setGrowthRate,
    onEdit,
    setShowApply,
    onReset,
  });

  useEffect(() => {
    setShowApply(true);
  }, [growthRate]);

  useEffect(() => {
    setEditing(isBudgetGenerated);
  }, [isBudgetGenerated]);

  useEffect(() => {
    if (editing && hasAnyChanged) {
      onChangedBudget(newBudget.flat());
    } else {
      onChangedBudget([]);
    }
  }, [newBudget, editing, hasAnyChanged, onChangedBudget]);

  useEffect(() => {
    if (editing === false) {
      setPreviousBudget(yearlyBudget);
    }
  }, [editing, yearlyBudget]);

  useEffect(() => {
    if (
      monthly.toString() !== newBudget.flat().toString() &&
      visibleYear !== year
    ) {
      setNewBudget(quarterBudgets);
      setVisibleYear(year);
    }
  }, [monthly, newBudget, quarterBudgets, visibleYear, year]);

  useEffect(() => {
    if (isRestored) {
      setNewBudget(quarterBudgets);
      setIsRestored((prevState) => !prevState);
    }
  }, [isRestored, quarterBudgets]);

  const restore = () => {
    onRestoreBudget(false);
    setIsRestored(true);
  };

  const enableSave =
    hasAnyChanged || isBudgetGenerated || hasAnyChangedComparedToCache;

  return (
    <>
      <FlexContainer justifyContent="space-between">
        <FlexContainer
          direction="row"
          gap={theme.spacing.spacing02}
          alignItems="start"
        >
          <YearlyBudget
            editing={editing}
            yearlyBudget={yearlyBudget}
            currency={currency}
            onYearlyBudgetChanged={yearlyBudgetChanged}
            onSetType={setLastEdited}
          />
          <When condition={editing}>
            <FlexContainer
              direction="column"
              alignItems="start"
              gap={theme.spacing.spacing01}
              css={{ width: "7rem" }}
              data-testid="growth-rate-cell"
            >
              <Text size="sm" tag="span" color={theme.color.text.text02}>
                Growth Rate
              </Text>
              <GrowthPlanNumberInput
                value={growthRate}
                setValue={setGrowthRate}
              />
            </FlexContainer>
          </When>
          <When condition={editing && growthRate !== 0}>
            <FlexContainer
              direction="column"
              alignItems="start"
              gap={theme.spacing.spacing03}
            >
              <Text size="sm" tag="span" color={theme.color.text.text02}>
                Yearly Budget modified by Growth Rate
              </Text>
              <FlexContainer gap={theme.spacing.spacing04}>
                <Text mb={0}>
                  {formatMoney(growthRateBudget, currency, false)}
                </Text>
                <When condition={showApply}>
                  <Button
                    size="sm"
                    severity="low"
                    data-testid="budget-apply"
                    onClick={applyGrowthRateBudget}
                  >
                    Apply to Months
                  </Button>
                </When>
              </FlexContainer>
            </FlexContainer>
          </When>
        </FlexContainer>
        <Can action={ACTIONS.updateBudget}>
          <BudgetBreakdownActions
            editing={editing}
            enableSave={enableSave}
            hasAnyChanged={hasAnyChanged}
            shouldAllowRestoration={shouldAllowRestoration}
            isBudgetGenerated={isBudgetGenerated}
            setEditing={setEditing}
            restoreBudget={restore}
            previousBudget={previousBudget}
            yearlyBudget={yearlyBudget}
            currency={currency}
            onCancel={cancel}
            onSave={saveBudget}
          />
        </Can>
      </FlexContainer>
      <Spacer height={theme.spacing.spacing06} />
      <When condition={editing && isBudgetGenerated}>
        <Message status="notification" image="info">
          <FlexContainer direction="column" alignItems="start">
            <Text
              weight="medium"
              color={theme.color.text.info}
              mb={theme.spacing.spacing02}
            >
              Budget has been generated automatically.
            </Text>
            <Text mb={theme.spacing.spacing00} color={theme.color.text.text02}>
              Please verify it before accepting.
            </Text>
          </FlexContainer>
        </Message>
        <Spacer height={theme.spacing.spacing06} />
      </When>
      <Wrapper>
        <Row border colored>
          {quarterlyBudgets.map((quarter, index) => (
            <Cell key={`quarter-${index}`} header>
              <Text
                tag="span"
                color={theme.color.text.text02}
                size="sm"
                weight="medium"
              >
                Quarter {index + 1}
              </Text>
              <Text
                tag="span"
                weight="medium"
                align="right"
                data-testid="budget-cell-quarter"
              >
                {formatMoney(quarter, currency, false)}
              </Text>
            </Cell>
          ))}
        </Row>
        <Row colored>
          {quarters.map((months, index) => (
            <Cell key={`quarter-${index}`}>
              {months.map((month) => (
                <Text
                  key={month}
                  tag="span"
                  align="center"
                  size="sm"
                  weight="medium"
                  color={theme.color.text.text02}
                >
                  {month}
                </Text>
              ))}
            </Cell>
          ))}
        </Row>
        <Row border>
          {newBudget.map((budgets, parentIndex) => (
            <Cell key={`quarter-${parentIndex}`}>
              {budgets.map((monthlyBudget, index) => (
                <BudgetCell
                  key={`${parentIndex * 3 + index}-${index}`}
                  monthlyBudget={monthlyBudget}
                  childrenBudget={childrenMonthly?.[parentIndex * 3 + index]}
                  editing={editing}
                  warningCaption={warningCaption}
                  hasChanged={hasChanged(parentIndex * 3 + index)}
                  currency={currency}
                  onSetType={setLastEdited}
                  onChange={(value) =>
                    updateBudget(value, parentIndex * 3 + index)
                  }
                />
              ))}
            </Cell>
          ))}
        </Row>
      </Wrapper>
    </>
  );
}

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr 1fr 1fr;
`;

type RowProps = {
  border?: boolean;
  colored?: boolean;
};

const Row = styled.div<RowProps>`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  ${handleRowStylesFromProps};
`;

const defaultBorder = `1px solid ${theme.color.border.border01}`;

type CellProps = {
  header?: boolean;
};

const Cell = styled.div<CellProps>`
  box-sizing: border-box;
  display: grid;
  justify-content: center;
  align-items: center;

  ${handleCellStylesFromProps};

  :not(:last-of-type) {
    border-right: ${defaultBorder};
  }
`;

function handleRowStylesFromProps({ border, colored }: RowProps) {
  return css({
    borderTop: border ? defaultBorder : "",
    borderBottom: border ? defaultBorder : "",
    backgroundColor: colored ? theme.color.background.ui03 : "transparent",
  });
}

function handleCellStylesFromProps({ header }: CellProps) {
  const { spacing } = theme;
  return css({
    gridTemplateColumns: header ? "repeat(2, 1fr)" : "repeat(3, 1fr)",
    padding: header
      ? `${spacing.spacing03} ${spacing.spacing04}`
      : `${spacing.spacing02} ${spacing.spacing01}`,
  });
}
